import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExportOutlined,
  PlusOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { Order, OrderStatus, OrderFilter, OrderResponse, OrdersResponse } from '../types/orders';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import {
  Input,
  Button,
  Table,
  Tag,
  Space,
  Tooltip,
  Typography,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  message,
  Modal,
  Form,
  InputNumber,
  Dropdown,
  Menu,
} from 'antd';
import orderService from '../services/orderService';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Hàm dịch trạng thái sang tiếng Việt
const translateStatus = (status: OrderStatus | string): string => {
  const statusMap: Record<string, string> = {
    initialized: 'Khởi tạo',
    confirmed: 'Đã xác nhận',
    processing: 'Đang xử lý',
    shipping: 'Đang giao hàng',
    delivered: 'Hoàn thành',
    cancelled: 'Đã hủy',
    rejected: 'Không nhận hàng',
    pending: 'Chờ xử lý',
    shipped: 'Đã giao hàng',
  };
  return statusMap[status] || status;
};

// Hàm dịch trạng thái thanh toán sang tiếng Việt
const translatePaymentStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'Chờ thanh toán',
    paid: 'Đã thanh toán',
    failed: 'Thanh toán thất bại',
    refunded: 'Đã hoàn tiền',
  };
  return statusMap[status] || status;
};

// Add interfaces for CreateOrderModal props and form values
interface CreateOrderModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateOrderFormValues) => void;
}

interface CreateOrderFormValues {
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  street: string;
  ward: string;
  district: string;
  city: string;
  items: Array<{
    productName: string;
    price: number;
    quantity: number;
  }>;
  paymentMethod: string;
  shippingMethod: string;
  shippingFee?: number;
  note?: string;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  visible,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values);
        form.resetFields();
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      visible={visible}
      title='Tạo đơn hàng mới'
      okText='Tạo đơn'
      cancelText='Hủy'
      onCancel={onCancel}
      onOk={handleSubmit}
      width={800}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <Form form={form} layout='vertical'>
        <h3 className='mb-3 font-semibold'>Thông tin khách hàng</h3>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='customerName'
              label='Tên khách hàng'
              rules={[
                { required: true, message: 'Vui lòng nhập tên khách hàng' },
              ]}
            >
              <Input placeholder='Nhập tên khách hàng' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='customerPhone'
              label='Số điện thoại'
              rules={[
                { required: true, message: 'Vui lòng nhập số điện thoại' },
              ]}
            >
              <Input placeholder='Nhập số điện thoại' />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name='customerEmail' label='Email'>
              <Input placeholder='Nhập email (không bắt buộc)' />
            </Form.Item>
          </Col>
        </Row>

        <h3 className='mt-4 mb-3 font-semibold'>Địa chỉ giao hàng</h3>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name='street'
              label='Địa chỉ'
              rules={[{ required: true, message: 'Vui lòng nhập địa chỉ' }]}
            >
              <Input placeholder='Số nhà, tên đường' />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='ward'
              label='Phường/Xã'
              rules={[{ required: true, message: 'Vui lòng nhập phường/xã' }]}
            >
              <Input placeholder='Phường/Xã' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='district'
              label='Quận/Huyện'
              rules={[{ required: true, message: 'Vui lòng nhập quận/huyện' }]}
            >
              <Input placeholder='Quận/Huyện' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='city'
              label='Tỉnh/Thành phố'
              rules={[
                { required: true, message: 'Vui lòng nhập tỉnh/thành phố' },
              ]}
            >
              <Input placeholder='Tỉnh/Thành phố' />
            </Form.Item>
          </Col>
        </Row>

        <h3 className='mt-4 mb-3 font-semibold'>Sản phẩm</h3>
        <Form.List name='items'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} className='pb-4 mb-4 border-b'>
                  <Col span={10}>
                    <Form.Item
                      {...restField}
                      name={[name, 'productName']}
                      rules={[
                        { required: true, message: 'Vui lòng chọn sản phẩm' },
                      ]}
                      label='Sản phẩm'
                    >
                      <Select
                        placeholder='Chọn sản phẩm'
                        showSearch
                        optionFilterProp='children'
                      >
                        <Select.Option value='1'>
                          Xoài cát Hòa Lộc
                        </Select.Option>
                        <Select.Option value='2'>
                          Táo Envy New Zealand
                        </Select.Option>
                        <Select.Option value='3'>Sầu riêng Ri6</Select.Option>
                        <Select.Option value='4'>Lê Hàn Quốc</Select.Option>
                        <Select.Option value='5'>
                          Hộp quà trái cây cao cấp
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      {...restField}
                      name={[name, 'price']}
                      rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                      label='Đơn giá'
                    >
                      <InputNumber
                        placeholder='Đơn giá'
                        style={{ width: '100%' }}
                        formatter={(value) =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      rules={[
                        { required: true, message: 'Vui lòng nhập số lượng' },
                      ]}
                      label='Số lượng'
                    >
                      <InputNumber
                        placeholder='Số lượng'
                        style={{ width: '100%' }}
                        min={1}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} className='flex items-end'>
                    <Button
                      type='text'
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => remove(name)}
                      className='mb-2'
                    >
                      Xóa
                    </Button>
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button
                  type='dashed'
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Thêm sản phẩm
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <h3 className='mt-4 mb-3 font-semibold'>Thanh toán & Giao hàng</h3>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='paymentMethod'
              label='Phương thức thanh toán'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn phương thức thanh toán',
                },
              ]}
            >
              <Select placeholder='Chọn phương thức thanh toán'>
                <Select.Option value='cod'>Tiền mặt (COD)</Select.Option>
                <Select.Option value='banking'>Chuyển khoản</Select.Option>
                <Select.Option value='momo'>Ví MoMo</Select.Option>
                <Select.Option value='zalopay'>ZaloPay</Select.Option>
                <Select.Option value='creditcard'>Thẻ tín dụng</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='shippingMethod'
              label='Phương thức vận chuyển'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn phương thức vận chuyển',
                },
              ]}
            >
              <Select placeholder='Chọn phương thức vận chuyển'>
                <Select.Option value='standard'>Tiêu chuẩn</Select.Option>
                <Select.Option value='express'>Nhanh</Select.Option>
                <Select.Option value='sameday'>Giao trong ngày</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name='shippingFee' label='Phí vận chuyển'>
              <InputNumber
                placeholder='Nhập phí vận chuyển'
                style={{ width: '100%' }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='note' label='Ghi chú'>
              <Input.TextArea rows={2} placeholder='Ghi chú cho đơn hàng' />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [currentFilter, setCurrentFilter] = useState<OrderFilter>({});
  const [selectedOrder, setSelectedOrder] = useState<OrderResponse | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] =
    useState<boolean>(false);
  const [isCreateOrderModalVisible, setIsCreateOrderModalVisible] =
    useState(false);
  const [isCustomExportModalVisible, setIsCustomExportModalVisible] =
    useState(false);
  const [customExportDateRange, setCustomExportDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  
  // Thêm state cho các filter mới
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  
  const { enqueueSnackbar } = useSnackbar();

  // Hàm lấy danh sách đơn hàng
  const fetchOrders = async (page: number = 1, filter: OrderFilter = {}) => {
    try {
      setLoading(true);

      // Tạo filter object với các params mới
      const filterParams: OrderFilter = {
        ...filter,
        page,
        limit: pageSize,
        search: searchText || undefined,
        orderStatus: orderStatusFilter !== 'all' ? (orderStatusFilter as 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'rejected') : undefined,
        paymentStatus: paymentStatusFilter !== 'all' ? (paymentStatusFilter as 'pending' | 'paid' | 'failed') : undefined,
        paymentMethod: paymentMethodFilter !== 'all' ? (paymentMethodFilter as 'COD' | 'VNPAY' | 'MOMO' | 'BANK' | 'OTHER') : undefined,
      };

      const response: OrdersResponse = await orderService.getOrders(filterParams);

      if (response.orders && response.orders.length > 0) {
        setOrders(response.orders);
        setTotalOrders(response.pagination.total);
      } else {
        setOrders([]);
        setTotalOrders(0);
      }
    } catch (error) {
      console.error('Lỗi khi lấy danh sách đơn hàng:', error);
      enqueueSnackbar('Không thể lấy danh sách đơn hàng', { variant: 'error' });
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  // Sử dụng useEffect để fetch orders
  useEffect(() => {
    const initialFilter: OrderFilter = {
      page: currentPage,
      limit: pageSize,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };

    setCurrentFilter(initialFilter);
    fetchOrders(currentPage, initialFilter);
  }, [currentPage, pageSize]);

  // Thêm useEffect để fetch orders khi các filter thay đổi
  useEffect(() => {
    fetchOrders(currentPage, currentFilter);
  }, [searchText, orderStatusFilter, paymentStatusFilter, paymentMethodFilter]);

  const filteredOrders = orders.filter((order) => {
    const matchesSearchTerm = searchText
      ? order.orderCode.includes(searchText) ||
        order.phone?.includes(searchText) ||
        order.email?.includes(searchText) ||
        order.shippingAddress
          ?.toLowerCase()
          .includes(searchText.toLowerCase())
      : true;

    return matchesSearchTerm;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'pending':
        return <Tag color='blue'>Chờ xử lý</Tag>;
      case 'processing':
        return <Tag color='gold'>Đang xử lý</Tag>;
      case 'shipped':
        return <Tag color='orange'>Đã giao hàng</Tag>;
      case 'delivered':
        return <Tag color='green'>Hoàn thành</Tag>;
      case 'cancelled':
        return <Tag color='red'>Đã hủy</Tag>;
      case 'rejected':
        return <Tag color='red'>Từ chối nhận hàng</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getPaymentStatusTag = (status: string) => {
    switch (status) {
      case 'pending':
        return <Tag color='orange'>Chờ thanh toán</Tag>;
      case 'paid':
        return <Tag color='green'>Đã thanh toán</Tag>;
      case 'failed':
        return <Tag color='red'>Thanh toán thất bại</Tag>;
      case 'refunded':
        return <Tag color='blue'>Đã hoàn tiền</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Handler xử lý cập nhật trạng thái đơn hàng
  const handleUpdateStatus = async (
    orderId: string,
    newStatus: OrderStatus,
    additionalData?: any
  ) => {
    try {
      setLoading(true);

      // Gọi service cập nhật trạng thái
      await orderService.updateStatus(orderId, newStatus, additionalData);

      // Cập nhật lại dữ liệu
      fetchOrders(currentPage, currentFilter);
      enqueueSnackbar(
        `Đã cập nhật trạng thái đơn hàng thành ${translateStatus(newStatus)}`,
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái đơn hàng:', error);
      enqueueSnackbar('Không thể cập nhật trạng thái đơn hàng', { variant: 'error' });
    } finally {
      setLoading(false);
      setSelectedOrder(null);
      fetchOrders(currentPage, currentFilter);
    }
  };

  // Hàm chuyển đổi từ Order sang OrderDetailCompatible
  const convertToCompatibleOrder = (order: OrderResponse) => {
    return order;
  };

  const handleViewOrderDetail = (order: OrderResponse) => {
    setSelectedOrder(convertToCompatibleOrder(order));
  };

  const closeOrderDetail = () => {
    setSelectedOrder(null);
    fetchOrders(currentPage, currentFilter);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchOrders(currentPage, currentFilter);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Thêm handlers cho các filter mới
  const handleOrderStatusFilterChange = (value: string) => {
    setOrderStatusFilter(value);
  };

  const handlePaymentStatusFilterChange = (value: string) => {
    setPaymentStatusFilter(value);
  };

  const handlePaymentMethodFilterChange = (value: string) => {
    setPaymentMethodFilter(value);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setOrderStatusFilter('all');
    setPaymentStatusFilter('all');
    setPaymentMethodFilter('all');
  };

  const handleCreateOrder = () => {
    setIsCreateOrderModalVisible(true);
  };

  const handleCreateOrderCancel = () => {
    setIsCreateOrderModalVisible(false);
  };

  const handleCreateOrderSubmit = (values: CreateOrderFormValues) => {
    enqueueSnackbar('Đã tạo đơn hàng mới', { variant: 'success' });
    setIsCreateOrderModalVisible(false);
  };

  // Tạm thời comment lại tính năng xuất Excel
  /*
  // Hàm xử lý xuất Excel theo các khoảng thời gian khác nhau
  const handleExport = async (type: 'day' | 'week' | 'month' | 'custom') => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    const today = new Date();

    switch (type) {
      case 'day':
        startDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Lấy ngày đầu tuần (thứ 2)
        startDate = new Date(today);
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Điều chỉnh khi chủ nhật
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);

        // Lấy ngày cuối tuần (chủ nhật)
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        // Lấy ngày đầu tháng
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);

        // Lấy ngày cuối tháng
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        // Mở modal cho người dùng chọn ngày
        setIsCustomExportModalVisible(true);
        return; // Ngừng xử lý, sẽ tiếp tục khi người dùng chọn xong ngày
    }

    console.log('startDate', startDate.toString());
    console.log('endDate', endDate.toString());

    try {
      const data = await orderService.getOrdersExport({
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });

      if (!data || !data.data || data.data.length === 0) {
        enqueueSnackbar('Không có dữ liệu để xuất Excel', { variant: 'warning' });
        return;
      }

      // Chuẩn bị dữ liệu cho Excel
      const excelData = data.data.map((order: any) => {
        return {
          'Mã đơn hàng': order.order_code,
          'Tên khách hàng': order.customer_name,
          'Số điện thoại': order.customer_phone,
          'Tên người nhận': order.receiver_name,
          'Số điện thoại người nhận': order.receiver_phone,
          'Chi nhánh': order.branch_name,
          'Tổng tiền': order.total_amount,
          'Phí ship': order.shipping_fee,
          'Phụ phí': order.surcharge,
          'Loại đơn':
            order.order_type === 'schedule'
              ? 'Giao sau'
              : order.order_type === 'pickup'
              ? 'Lấy hàng'
              : 'Giao ngay',
          'Trạng thái': translateStatus(order.status),
          'Ngày tạo': new Date(order.created_at).toLocaleString('vi-VN'),
          'Ghi chú': order.notes || '',
          'Mã giao hàng': order.notes?.delivery_code || '',
        };
      });

      // Tạo workbook và worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Đơn hàng');

      // Tạo tên file với ngày tháng
      const fileName = `DonHang_${
        type === 'day' ? 'Ngay' : type === 'week' ? 'Tuan' : 'Thang'
      }_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Xuất file Excel
      XLSX.writeFile(wb, fileName);

      enqueueSnackbar(
        `Đã xuất Excel theo ${
          type === 'day' ? 'ngày' : type === 'week' ? 'tuần' : 'tháng'
        }`,
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error exporting orders:', error);
      enqueueSnackbar('Có lỗi xảy ra khi xuất Excel', { variant: 'error' });
    }
  };
  */

  // Tạm thời comment lại tính năng xuất Excel
  /*
  // Hàm xử lý xuất Excel theo ngày tùy chỉnh
  const handleCustomExport = async () => {
    if (!customExportDateRange[0] || !customExportDateRange[1]) {
      enqueueSnackbar('Vui lòng chọn khoảng thời gian', { variant: 'error' });
      return;
    }

    const startDate = customExportDateRange[0].toDate();
    startDate.setHours(0, 0, 0, 0);

    const endDate = customExportDateRange[1].toDate();
    endDate.setHours(23, 59, 59, 999);

    console.log('startDate', startDate.toString());
    console.log('endDate', endDate.toString());

    try {
      const data = await orderService.getOrdersExport({
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });

      if (!data || !data.data || data.data.length === 0) {
        enqueueSnackbar('Không có dữ liệu để xuất Excel', { variant: 'warning' });
        return;
      }

      // Chuẩn bị dữ liệu cho Excel
      const excelData = data.data.map((order: any) => {
        return {
          'Mã đơn hàng': order.order_code,
          'Tên khách hàng': order.customer_name,
          'Số điện thoại': order.customer_phone,
          'Tên người nhận': order.receiver_name,
          'Số điện thoại người nhận': order.receiver_phone,
          'Chi nhánh': order.branch_name,
          'Tổng tiền': order.total_amount,
          'Phí ship': order.shipping_fee,
          'Phụ phí': order.surcharge,
          'Loại đơn':
            order.order_type === 'schedule'
              ? 'Giao sau'
              : order.order_type === 'pickup'
              ? 'Lấy hàng'
              : 'Giao ngay',
          'Trạng thái': translateStatus(order.status),
          'Ngày tạo': new Date(order.created_at).toLocaleString('vi-VN'),
          'Ghi chú': order.notes || '',
          'Mã giao hàng': order.notes?.delivery_code || '',
        };
      });

      // Tạo workbook và worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Đơn hàng');

      // Tạo tên file với khoảng ngày tùy chỉnh
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const fileName = `DonHang_TuyChinh_${startDateStr}_den_${endDateStr}.xlsx`;

      // Xuất file Excel
      XLSX.writeFile(wb, fileName);

      enqueueSnackbar('Đã xuất Excel theo khoảng thời gian tùy chỉnh', { variant: 'success' });
      setIsCustomExportModalVisible(false);
      setCustomExportDateRange([null, null]);
    } catch (error) {
      console.error('Error exporting orders:', error);
      enqueueSnackbar('Có lỗi xảy ra khi xuất Excel', { variant: 'error' });
    }
  };
  */

  // Cấu hình các cột cho bảng
  const columns = [
    {
      title: 'Mã đơn',
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 120,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Khách hàng',
      key: 'customer',
      width: 200,
      render: (record: OrderResponse) => (
        <div>
          <div className="font-medium">{record.email}</div>
          <Text type='secondary' style={{ fontSize: '12px' }}>
            {record.phone}
          </Text>
        </div>
      ),
    },
    {
      title: 'Tổng tiền',
      key: 'total',
      width: 150,
      render: (record: OrderResponse) => {
        return (
          <div>
            <div className="font-medium">{formatCurrency(record.priceDetail.totalPrice)}</div>
            <div className="text-xs text-gray-500">
              Phí ship: {formatCurrency(record.priceDetail.shippingFee)}
            </div>
            {record.priceDetail.discountPrice > 0 && (
              <div className="text-xs text-black">
                Giảm: {formatCurrency(record.priceDetail.discountPrice)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Trạng thái đơn',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Trạng thái thanh toán',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 140,
      render: (status: string) => getPaymentStatusTag(status),
    },
    {
      title: 'Phương thức thanh toán',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 100,
      render: (method: string) => (
        <Tag color="blue">{method}</Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: OrderResponse) => (
        <Tooltip title='Xem chi tiết'>
          <Button
            type='primary'
            ghost
            size='small'
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation(); // Ngăn click event lan tỏa đến hàng
              handleViewOrderDetail(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className='p-4'>
      {/* Header row với bộ lọc */}
      <Card className='mb-4'>
        <Row justify='space-between' align='middle' gutter={16}>
          <Col>
            <Title level={2} className='m-0'>
              Quản lý đơn hàng
            </Title>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder='Tìm kiếm theo mã đơn, email, số điện thoại...'
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={handleSearchChange}
                style={{ width: '300px' }}
              />
              <Button
                icon={<FilterOutlined />}
                onClick={() => setIsDetailModalVisible(!isDetailModalVisible)}
              >
                Bộ lọc
              </Button>
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={handleRefresh}
                disabled={loading}
              />
              {/* Tạm thời comment lại tính năng xuất Excel
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item key='day' onClick={() => handleExport('day')}>
                      Xuất theo ngày
                    </Menu.Item>
                    <Menu.Item key='week' onClick={() => handleExport('week')}>
                      Xuất theo tuần
                    </Menu.Item>
                    <Menu.Item
                      key='month'
                      onClick={() => handleExport('month')}
                    >
                      Xuất theo tháng
                    </Menu.Item>
                    <Menu.Item
                      key='custom'
                      onClick={() => handleExport('custom')}
                    >
                      Xuất theo khoảng thời gian tùy chỉnh
                    </Menu.Item>
                  </Menu>
                }
                placement='bottomRight'
              >
                <Button
                  type='primary'
                  icon={<ExportOutlined />}
                  className='bg-green-600 border-green-600 hover:bg-green-700'
                >
                  Xuất Excel <DownOutlined />
                </Button>
              </Dropdown>
              */}
            </Space>
          </Col>
        </Row>

        {isDetailModalVisible && (
          <div className='pt-3 mt-3 border-t'>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <div className='mb-3'>
                  <label className='block mb-1 text-sm font-medium text-gray-700'>
                    Trạng thái đơn hàng
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={orderStatusFilter}
                    onChange={handleOrderStatusFilterChange}
                    placeholder="Chọn trạng thái đơn hàng"
                  >
                    <Select.Option value="all">Tất cả</Select.Option>
                    <Select.Option value="pending">Chờ xử lý</Select.Option>
                    <Select.Option value="processing">Đang xử lý</Select.Option>
                    <Select.Option value="shipped">Đã giao hàng</Select.Option>
                    <Select.Option value="delivered">Hoàn thành</Select.Option>
                    <Select.Option value="cancelled">Đã hủy</Select.Option>
                    <Select.Option value="rejected">Từ chối nhận hàng</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col span={6}>
                <div className='mb-3'>
                  <label className='block mb-1 text-sm font-medium text-gray-700'>
                    Trạng thái thanh toán
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={paymentStatusFilter}
                    onChange={handlePaymentStatusFilterChange}
                    placeholder="Chọn trạng thái thanh toán"
                  >
                    <Select.Option value="all">Tất cả</Select.Option>
                    <Select.Option value="pending">Chờ thanh toán</Select.Option>
                    <Select.Option value="paid">Đã thanh toán</Select.Option>
                    <Select.Option value="failed">Thanh toán thất bại</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col span={6}>
                <div className='mb-3'>
                  <label className='block mb-1 text-sm font-medium text-gray-700'>
                    Phương thức thanh toán
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={paymentMethodFilter}
                    onChange={handlePaymentMethodFilterChange}
                    placeholder="Chọn phương thức thanh toán"
                  >
                    <Select.Option value="all">Tất cả</Select.Option>
                    <Select.Option value="COD">Tiền mặt (COD)</Select.Option>
                    <Select.Option value="VNPAY">VNPAY</Select.Option>
                    <Select.Option value="MOMO">MOMO</Select.Option>
                    <Select.Option value="BANK">Chuyển khoản</Select.Option>
                    <Select.Option value="OTHER">Khác</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col span={6} className="flex items-end">
                <Button 
                  type="default" 
                  onClick={handleClearFilters}
                  style={{ marginBottom: '12px' }}
                >
                  Xóa bộ lọc
                </Button>
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {/* Bảng đơn hàng */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredOrders.map((order) => ({
            ...order,
            key: order._id,
          }))}
          loading={loading}
          pagination={{
            pageSize: pageSize,
            total: totalOrders,
            current: currentPage,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} đơn hàng`,
            onChange: (page) => {
              setCurrentPage(page);
              fetchOrders(page, currentFilter);
            },
          }}
          onRow={(record) => ({
            onClick: () => handleViewOrderDetail(record),
            style: { cursor: 'pointer' },
          })}
          bordered
        />
      </Card>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder as any}
          onClose={closeOrderDetail}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {/* Create Order Modal */}
      <CreateOrderModal
        visible={isCreateOrderModalVisible}
        onCancel={handleCreateOrderCancel}
        onSubmit={handleCreateOrderSubmit}
      />

      {/* Tạm thời comment lại tính năng xuất Excel
      Custom Export Date Range Modal
      <Modal
        title='Chọn khoảng thời gian xuất báo cáo'
        visible={isCustomExportModalVisible}
        onOk={handleCustomExport}
        onCancel={() => {
          setIsCustomExportModalVisible(false);
          setCustomExportDateRange([null, null]);
        }}
        okText='Xuất Excel'
        cancelText='Hủy'
      >
        <div className='mb-4'>
          <label className='block mb-2 text-sm font-medium'>
            Chọn khoảng thời gian:
          </label>
          <RangePicker
            style={{ width: '100%' }}
            value={customExportDateRange}
            onChange={(dates) =>
              setCustomExportDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])
            }
          />
        </div>
      </Modal>
      */}
    </div>
  );
};

export default Orders;
